/**
 * @fileoverview This component is used to render the "Browse By Standards"
 * link in the courses menu. If the conditions are met to render the link, it
 * will be displayed as the first pinned link in the menu.
 */
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import Link from "@khanacademy/wonder-blocks-link";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {doNotTranslateYet} from "@khan/i18n";
import {useLocation} from "@khan/urls";

import {coursesMenuCoreStyles as styles} from "./shared-styles.ts";
import type {ItemsType} from "./types.ts";

import externalLinkIcon from "@phosphor-icons/core/bold/arrow-square-out-bold.svg";

type Props = {
    // The position index of the link in the main menu
    posIdx: number;
    // The ref object for the main menu items
    mainItemRefs: React.MutableRefObject<ItemsType>;
    // Whether the link is selected
    isSelected: boolean;
};

export const StandardsGuidesLink = ({
    posIdx,
    mainItemRefs,
    isSelected,
}: Props): React.ReactElement | null => {
    const href = "/regions/united-states";
    const translatedTitle = doNotTranslateYet`Browse By Standards`;
    const location = useLocation();

    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    const handleOnClick = async () => {
        await publishAnalyticsEvent({
            eventType: "NavBarCourseMenuStandardsClicked",
            currentPageUrlPath: location.pathname,
            eventSchemaVersion: 1,
        });
    };

    return (
        // Parent provides the <li>; avoid nested list items here
        <Link
            safeWithNav={handleOnClick}
            href={href}
            target="_blank"
            style={styles.menuItem}
            endIcon={<PhosphorIcon icon={externalLinkIcon} size="small" />}
            ref={(node: HTMLAnchorElement) => {
                mainItemRefs.current[posIdx] = node;
            }}
            role="menuitem"
            tabIndex={isSelected ? 0 : -1}
        >
            <LabelMedium style={styles.pinnedLinkText}>
                {translatedTitle}
            </LabelMedium>
        </Link>
    );
};
